/**
 * simulator/pages/autoescuela/autoescuelaBillingStore.js
 * Store EN MEMORIA de facturación de autoescuela para el simulador de ventas.
 * Modelos: almacenStore.js y adminLockStore.js.
 *
 * Estado:
 *   concepts  — catálogo de conceptos facturables (nombre + precio)
 *   invoices  — facturas emitidas en la sesión (mock, sin backend)
 *   fiscal    — datos fiscales de la empresa (razon_social, nif, address, iva_rate)
 *   series    — plantilla de serie (p.ej. "AE-{YYYY}-{N:4}")
 *   _seq      — contador correlativo
 *
 * Nunca importa axiosClient ni usa fetch/XMLHttpRequest.
 *
 * Datos de EJEMPLO (8/10/2026, facturacionEjemplo.js): al cargar se siembran facturas, cuentas de alumnos y facturas
 * recibidas relativas a hoy, marcadas `ejemplo: true`; lo que se crea en la demo convive con ellas. reset() lo vacía
 * todo (tests); sembrarFacturacionEjemplo() lo vuelve a sembrar sin tocar lo creado en la sesión.
 */
import { buildFacturacionEjemplo } from "./facturacionEjemplo";

// ── Estado de módulo (en memoria) ─────────────────────────────────────────────

const DEFAULT_CONCEPTS = [
  { id: "c1", nombre: "Práctica ciudad",      precio: 30 },
  { id: "c2", nombre: "Práctica carretera",   precio: 35 },
  { id: "c3", nombre: "Práctica autovía",     precio: 40 },
  { id: "c4", nombre: "Examen DGT",           precio: 50 },
  { id: "c5", nombre: "Tasas administrativas",precio: 80 },
  { id: "c6", nombre: "Pack 5 prácticas",     precio: 140 },
  { id: "c7", nombre: "Pack 10 prácticas",    precio: 270 },
];

let _concepts = DEFAULT_CONCEPTS.map((c) => ({ ...c }));
let _invoices = [];
// Razón social de EJEMPLO: la que se ve si se entra al simulador sin la ficha de un lead.
export const RAZON_SOCIAL_EJEMPLO = "Autoescuela Conduce Bien SL";
let _fiscal   = {
  razon_social: RAZON_SOCIAL_EJEMPLO,
  nif:          "B77889900",
  address:      "Av. del Volante 15, 28010 Madrid",
  iva_rate:     21,
};
// true cuando el comercial ha cambiado la razón social a mano (Facturación o «Nombre del negocio»): volver a entrar
// desde la MISMA ficha no se la pisa. `_leadAplicado`: el nombre de la ficha con la que se entró la última vez.
let _razonSocialManual = false;
let _leadAplicado = null;
let _series = null;   // null = no configurada todavía
let _seq    = 0;

const _subs = new Set();
const _emit = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });

// ── Suscripción ───────────────────────────────────────────────────────────────

/** Suscribe un callback a cualquier cambio del store. Devuelve la función de desuscripción. */
export function subscribe(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

// ── Conceptos ────────────────────────────────────────────────────────────────

export function getConcepts() {
  return _concepts.slice();
}

export function setConcepts(list) {
  _concepts = (list || []).map((c) => ({ ...c }));
  _emit();
}

/** Añade un concepto nuevo al catálogo. */
export function addConcept({ nombre, precio }) {
  const id = `c${Date.now()}`;
  _concepts = [..._concepts, { id, nombre: String(nombre || "").trim(), precio: Number(precio) || 0 }];
  _emit();
}

/** Actualiza un concepto existente por id. */
export function updateConcept(id, patch) {
  _concepts = _concepts.map((c) => (c.id === id ? { ...c, ...patch } : c));
  _emit();
}

/** Elimina un concepto por id. */
export function deleteConcept(id) {
  _concepts = _concepts.filter((c) => c.id !== id);
  _emit();
}

// ── Datos fiscales ────────────────────────────────────────────────────────────

export function getFiscal() {
  return { ..._fiscal };
}

export function setFiscal(data) {
  if (data && data.razon_social !== undefined && String(data.razon_social).trim() !== String(_fiscal.razon_social || "").trim()) {
    _razonSocialManual = true;
  }
  _fiscal = { ..._fiscal, ...data };
  _emit();
}

/**
 * Al entrar al simulador (SimulatorContext.setSector): con la ficha de un lead, la razón social pasa a ser el
 * NOMBRE de su autoescuela (`contactName` = c_contactos.nombre, no la persona de contacto); sin ficha, la de
 * ejemplo. De ella sale getBusinessName(): prefijo «📩 *X* te informa:», chats, imagen de la práctica, contrato,
 * facturas y el `business_name` que se manda de verdad al móvil del lead. Si el comercial la cambió a mano para
 * esta misma ficha, se respeta; otra ficha (u otra demo sin ficha) empieza de cero.
 */
export function aplicarNombreLeadNegocio(contactName) {
  const nombre = String(contactName == null ? "" : contactName).replace(/\s+/g, " ").trim();
  if (nombre !== _leadAplicado) {
    _leadAplicado = nombre;
    _razonSocialManual = false;
  }
  if (_razonSocialManual) return;
  const next = nombre || RAZON_SOCIAL_EJEMPLO;
  if (next === _fiscal.razon_social) return;
  _fiscal = { ..._fiscal, razon_social: next };
  _emit();
}

// ── Serie de facturación ──────────────────────────────────────────────────────

export function getSeries() {
  return _series;
}

export function setSeries(s) {
  _series = s;
  _emit();
}

// ── Facturas ─────────────────────────────────────────────────────────────────

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** Genera el siguiente número de factura. Formato: AE-YYYY-XXXX (o la plantilla configurada). */
function nextNumber() {
  _seq += 1;
  const year = new Date().getFullYear();
  if (_series && typeof _series.template === "string") {
    return _series.template
      .replace(/\{YYYY\}/g, String(year))
      .replace(/\{YY\}/g, String(year).slice(-2))
      .replace(/\{N(?::([1-9]))?\}/g, (_, w) =>
        w ? String(_seq + (_series.next || 1) - 1).padStart(Number(w), "0") : String(_seq),
      );
  }
  return `AE-${year}-${String(_seq).padStart(4, "0")}`;
}

/**
 * Registra una factura mock.
 * @param {object} opts
 * @param {object}   opts.client  — { nombre, phone, nif }
 * @param {Array}    opts.lines   — [{ name, quantity, unit_price, discount_pct }]
 * @param {string}   opts.format  — "ticket" | "a4"
 * @param {string}   [opts.worker_name]
 * @returns {object} La factura creada (con number, invoice_id, total, etc.)
 */
export function addMockInvoice({ client, lines, format, worker_name }) {
  const isoNow = new Date().toISOString();
  const linesFull = (lines || []).map((l) => {
    const qty  = Number(l.quantity)  || 1;
    const price = Number(l.unit_price) || 0;
    const disc  = Math.min(100, Math.max(0, Number(l.discount_pct) || 0));
    const total = round2(qty * price * (1 - disc / 100));
    return { ...l, quantity: qty, unit_price: price, discount_pct: disc, total };
  });
  const grandTotal = round2(linesFull.reduce((s, l) => s + l.total, 0));
  const ivaRate = Number(_fiscal.iva_rate) || 21;
  const base    = round2(grandTotal / (1 + ivaRate / 100));
  const iva     = round2(grandTotal - base);

  const invoice = {
    invoice_id:  `ae-mock-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    number:      nextNumber(),
    source:      "autoescuela",
    format:      format || "ticket",
    client_name: (client && client.nombre) || "Alumno",
    client_phone:(client && client.phone)  || "",
    client_nif:  (client && client.nif)    || "",
    lines:       linesFull,
    total:       grandTotal,
    base,
    iva,
    iva_rate:    ivaRate,
    worker_name: worker_name || "",
    created_at:  isoNow,
    // VeriFactu (chip en Facturación): la factura nace «pendiente» y a los ~2 s Hacienda la acepta,
    // como en la demo de citas. Solo es la maqueta de lo que enseña la app real.
    verifactu_status: "pending",
  };

  _invoices = [invoice, ..._invoices];
  _emit();
  setTimeout(() => {
    _invoices = _invoices.map((x) => (x.invoice_id === invoice.invoice_id && x.verifactu_status === "pending" ? { ...x, verifactu_status: "accepted" } : x));
    _emit();
  }, 2000);
  return invoice;
}

export function listInvoices() {
  return _invoices.slice();
}

// ── Packs de prácticas ───────────────────────────────────────────────────────

let _packs = [];                     // { bono_id, alumno_id, name, sessions_total, sessions_used }
const _redemptions = new Map();      // redemption_id → { alumno_id, pack (ref) }

function _uid(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Crea un pack de prácticas para un alumno y emite una factura ticket automáticamente.
 * @param {{ alumno_id: string, sessions_total: number, name: string, price: number, alumno_name?: string }} opts
 * @returns {object} El pack creado.
 */
export function createPack({ alumno_id, sessions_total, name, price, alumno_name, sinFactura = false }) {
  const pack = {
    bono_id:        _uid("bono"),
    alumno_id:      String(alumno_id || ""),
    name:           String(name || `Pack ${sessions_total} prácticas`),
    sessions_total: Number(sessions_total) || 0,
    sessions_used:  0,
    created_at:     new Date().toISOString(),
  };
  _packs = [..._packs, pack];

  // Pack a 0 € de la garantía de aprobado: como en c_bonos, sin factura ni caja.
  if (sinFactura) { _emit(); return pack; }

  // Facturar automáticamente el pack como ticket
  addMockInvoice({
    client: { nombre: alumno_name || alumno_id || "Alumno" },
    lines: [{ name: pack.name, unit_price: Number(price) || 0, quantity: 1 }],
    format: "ticket",
  });

  return pack;
}

/**
 * Devuelve el pack activo del alumno (con saldo > 0), o null si no tiene.
 */
/** ¿Tuvo pack y ya no le queda ninguna sesión? (10/10/2026: «bono agotado» del planificador y del Resumen). */
export function packAgotado(alumno_id) {
  return _packs.some((p) => p.alumno_id === String(alumno_id)) && !packBalance(alumno_id);
}

export function packBalance(alumno_id) {
  return (
    _packs.find(
      (p) => p.alumno_id === String(alumno_id) && (p.sessions_total - p.sessions_used) > 0,
    ) || null
  );
}

/**
 * Descuenta 1 sesión del pack activo del alumno.
 * @param {string} alumno_id
 * @returns {{ redemption_id: string, pack: object } | null}
 */
export function redeemPack(alumno_id) {
  const pack = packBalance(alumno_id);
  if (!pack) return null;
  pack.sessions_used += 1;
  const redemption_id = _uid("red");
  _redemptions.set(redemption_id, { alumno_id: String(alumno_id), pack });
  _packs = [..._packs]; // trigger new reference so components re-render
  _emit();
  return { redemption_id, pack };
}

/**
 * Revierte un canje previo (al cancelar la práctica).
 * @param {string} redemption_id
 */
export function reversePack(redemption_id) {
  const entry = _redemptions.get(redemption_id);
  if (!entry) return;
  entry.pack.sessions_used = Math.max(0, entry.pack.sessions_used - 1);
  _redemptions.delete(redemption_id);
  _packs = [..._packs];
  _emit();
}

/**
 * Descuenta n sesiones de un pack concreto (3/11/2026, «Descontar las prácticas que ya tiene sin pack» al venderlo).
 * Nunca pasa del total. Devuelve las descontadas.
 */
export function descontarDePack(bono_id, n) {
  const pack = _packs.find((x) => x.bono_id === bono_id);
  if (!pack) return 0;
  const k = Math.max(0, Math.min(Number(n) || 0, pack.sessions_total - pack.sessions_used));
  pack.sessions_used += k;
  _packs = [..._packs];
  _emit();
  return k;
}

export function listPacks() {
  return _packs.slice();
}

// ── Movimientos de cuenta del alumno (cargos / pagos) ────────────────────────
// Estructura: { mov_id, alumno_id, alumno_name, tipo, concepto, importe_cents, fecha, metodo? }

let _movimientos = []; // [{ mov_id, alumno_id, alumno_name, tipo, concepto, importe_cents, fecha, metodo?, source? }]
let _movSeq = 0;

const MOCK_ALUMNO_IDS = ["a1", "a2", "a3"]; // ids en sync con mockData

/** saldo en céntimos: Σcargos − Σpagos. Positivo = deuda del alumno. */
export function computeSaldo(items) {
  let s = 0;
  for (const it of items || []) {
    const c = Number(it.importe_cents);
    if (!Number.isFinite(c)) continue;
    if (it.tipo === "cargo")  s += c;
    else if (it.tipo === "pago") s -= c;
  }
  return s;
}

/**
 * Añade un movimiento (cargo o pago) para un alumno. `ref` (opcional) identifica de dónde viene (p. ej. la práctica
 * reservada en el móvil del lead) para no apuntarlo dos veces (findMovimientoByRef).
 */
export function addMovimiento({ alumno_id, alumno_name, tipo, concepto, importe_cents, fecha, metodo, source, ref, bono_id }) {
  _movSeq += 1;
  const mov = {
    mov_id:       `mov-${Date.now()}-${_movSeq}`,
    alumno_id:    String(alumno_id || ""),
    alumno_name:  String(alumno_name || ""),
    tipo:         tipo === "pago" ? "pago" : "cargo",
    concepto:     String(concepto || "").slice(0, 200),
    importe_cents: Math.round(Number(importe_cents) || 0),
    fecha:        fecha || new Date().toISOString().slice(0, 10),
    created_at:   new Date().toISOString(),
    ...(metodo ? { metodo } : {}),
    ...(source  ? { source } : {}),
    ...(ref     ? { ref: String(ref) } : {}),
    // El pack al que va (4/11/2026, «Sin pagar»; como c_putMovimiento).
    ...(bono_id ? { bono_id: String(bono_id) } : {}),
  };
  _movimientos = [mov, ..._movimientos];
  _emit();
  return mov;
}

/** Elimina un movimiento por mov_id. */
export function deleteMovimiento(mov_id) {
  _movimientos = _movimientos.filter((m) => m.mov_id !== mov_id);
  _emit();
}

/** Lista de movimientos de un alumno, ordenados del más reciente al más antiguo. */
export function listMovimientos(alumno_id) {
  return _movimientos.filter((m) => m.alumno_id === String(alumno_id));
}

/** Todos los movimientos de todos los alumnos, del más reciente al más antiguo. */
export function listAllMovimientos() {
  return _movimientos.slice();
}

/** El movimiento con esa `ref` (y ese tipo, si se da), o null. */
export function findMovimientoByRef(ref, tipo) {
  if (!ref) return null;
  return _movimientos.find((m) => m.ref === String(ref) && (!tipo || m.tipo === tipo)) || null;
}

/** Pago cobrado online (por el enlace de WhatsApp o con «Cobrar online»): lleva comisión y desglose. */
export const esPagoOnline = (m) => !!m && m.tipo === "pago" && (m.metodo === "whatsapp" || m.metodo === "stripe");

/** mov_id del PRIMER cobro online de la autoescuela (el que tarda 7 días en llegar al banco), o null. */
export function primerCobroOnlineId() {
  for (let i = _movimientos.length - 1; i >= 0; i -= 1) {
    if (esPagoOnline(_movimientos[i])) return _movimientos[i].mov_id;
  }
  return null;
}

/** Saldo de un alumno en céntimos. */
export function saldoAlumno(alumno_id) {
  return computeSaldo(listMovimientos(alumno_id));
}

/** Resumen global: total_pendiente_cents + alumnos con deuda. */
export function getSaldoSummary(alumnosMap) {
  // alumnosMap: { [alumno_id]: name } — opcional; si no se pasa, usa alumno_name del primer mov
  const byAlumno = {};
  for (const m of _movimientos) {
    if (!byAlumno[m.alumno_id]) {
      byAlumno[m.alumno_id] = { alumno_id: m.alumno_id, name: (alumnosMap && alumnosMap[m.alumno_id]) || m.alumno_name, items: [] };
    }
    byAlumno[m.alumno_id].items.push(m);
  }
  let total_pendiente_cents = 0;
  const alumnos = [];
  for (const entry of Object.values(byAlumno)) {
    const saldo_cents = computeSaldo(entry.items);
    if (saldo_cents > 0) {
      total_pendiente_cents += saldo_cents;
      alumnos.push({ alumno_id: entry.alumno_id, name: entry.name, saldo_cents });
    }
  }
  return { total_pendiente_cents, alumnos };
}

// ── Cobros online (simulador) ─────────────────────────────────────────────────

let _onlinePaymentsEnabled = false;

/** Devuelve si los cobros online están "activados" en el simulador. */
export function getOnlinePaymentsEnabled() {
  return _onlinePaymentsEnabled;
}

/** Activa o desactiva los cobros online en el simulador (sin backend). */
export function setOnlinePaymentsEnabled(enabled) {
  _onlinePaymentsEnabled = !!enabled;
  _emit();
}

// ── Facturas recibidas (gastos) ──────────────────────────────────────────────
// Las guarda la pestaña «Facturas recibidas» (MockFacturasRecibidasTab, controlada con initialItems/onItemsChange)
// para que lo dado de alta en la demo siga ahí al cambiar de pestaña. Sin _emit: solo la lee esa pestaña.

let _gastos = [];

export function listGastos() {
  return _gastos.slice();
}

export function setGastos(list) {
  _gastos = Array.isArray(list) ? list.slice() : [];
}

// ── Datos de ejemplo ─────────────────────────────────────────────────────────

/**
 * (Re)siembra los datos de ejemplo relativos a `now`: sustituye los `ejemplo: true` anteriores y conserva lo creado en
 * la sesión. La serie sigue tras la última factura de ejemplo del año.
 */
export function sembrarFacturacionEjemplo(now = new Date()) {
  const seed = buildFacturacionEjemplo(now);
  const propias = (list) => list.filter((x) => !x.ejemplo);
  _invoices = [...propias(_invoices), ...seed.invoices].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  _movimientos = [...propias(_movimientos), ...seed.movimientos];
  _gastos = [...propias(_gastos), ...seed.gastos];
  _seq = Math.max(_seq, seed.lastSeqThisYear);
  _emit();
}

sembrarFacturacionEjemplo();

// ── Reset (para tests / re-demo) ─────────────────────────────────────────────

export function reset() {
  _concepts = DEFAULT_CONCEPTS.map((c) => ({ ...c }));
  _invoices = [];
  _fiscal   = {
    razon_social: RAZON_SOCIAL_EJEMPLO,
    nif:          "B77889900",
    address:      "Av. del Volante 15, 28010 Madrid",
    iva_rate:     21,
  };
  _razonSocialManual = false;
  _leadAplicado = null;
  _series  = null;
  _seq     = 0;
  _packs   = [];
  _redemptions.clear();
  _movimientos = [];
  _movSeq  = 0;
  _gastos  = [];
  _onlinePaymentsEnabled = false;
  _emit();
}

export { MOCK_ALUMNO_IDS };
